<script setup>
import { ref, reactive, computed, watch, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";

import { Search as SearchIcon, X, ChevronDown, Check } from "lucide-vue-next";
import TableFooterWrapper from "@/shared/ui/custom/components/TableFooterWrapper.vue";
import BottomSheet from "@/shared/ui/bottom-sheet/BottomSheet.vue";
import dateFormat from "@/shared/lib/date-format";
import { getParticipantNotarialList } from "@/entities/transaction";
import { getReferenceListByCodes } from "@/entities/directory";
import { getDirectoryByParentAllChildren } from "@/entities/directory";
import { getMe } from "@/entities/session";
import ClientCabinetDocumentCard from "@/pages/admin/client_cabinet/components/mobile/ClientCabinetDocumentCard.vue";
import ClientCabinetProfile from "@/pages/admin/client_cabinet/components/ClientCabinetProfile.vue";
import ClientCabinetQuickActions from "@/pages/admin/client_cabinet/components/ClientCabinetQuickActions.vue";
import ClientCabinetFilter from "@/pages/admin/client_cabinet/components/ClientCabinetFilter.vue";
import {
  ensureStatusDictionary,
  getStatusDictionary,
  getStatusLabel as getStatusLabelFromDict,
  getStatusColor as getStatusColorFromDict,
} from "@/shared/config/statusDictionary";
import StatusDot from "@/shared/ui/status-dot/StatusDot.vue";
import { getDirectoryItem } from "@/entities/directory";

import Button from "@/shared/ui/button/Button.vue";
import Radio from "@/shared/ui/radio/Radio.vue";
import RadioGroup from "@/shared/ui/radio/RadioGroup.vue";
import { FormItem, FormLabel, FormControl } from "@/shared/ui/form";
import { Input } from "@/shared/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "@/shared/ui/popover";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from "@/shared/ui/pagination";
import {
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/ui/table";
import { Spinner } from "@/shared/ui/spinner";

const router = useRouter();
const store = useStore();
const { t } = useI18n();

const loading = ref(false);
const list = ref([]);
const references = ref([]);
const total = ref(0);
const filters = reactive({
  template_code: null,
  status: null,
  reg_date: null,
  category_code: null,
  page: 1,
  size: 20,
});
const sortType = ref("created_at_desc");
const tempSortType = ref("created_at_desc");
const tempFilters = reactive({
  status: null,
  reg_date: null,
  category_code: null,
  template_code: null,
});
const directories = ref([]);
const directoriesLoading = ref(false);
const directoriesPage = ref(1);
const directoriesPageSize = ref(20);
const directoriesHasMore = ref(false);
const directoriesLoadingMore = ref(false);
const directoriesSearchQuery = ref("");
const directoriesSearchTimer = ref(null);
const directorySearchTerm = ref("");
const directoryPopoverOpen = ref(false);
const showFilters = ref(false);
const showFilterSheet = ref(false);
const showSortSheet = ref(false);
const user = ref({ fullName: "", iin: "" });
const quickActions = ref([
  {
    titleLines: ["AI-помощник"],
    subtitle: "Консультация нотариуса",
    icon: "huge-ai",
    action: "ai_chat",
  },
  {
    titleLines: ["Заказать услугу онлайн"],
    subtitle: "Цифровые документы",
    icon: "huge-message-box",
  },
  {
    titleLines: ["Исполнительные", "надписи"],
    subtitle: "В отношении Вас",
    icon: "huge-message",
  },
]);

function handleQuickAction(action) {
  if (action.action === "ai_chat") {
    router.push({ name: "client_ai_chat" });
  }
}
const statusOptions = computed(() => {
  const dict = getStatusDictionary().value;
  if (dict.size === 0) return [];
  return Array.from(dict.values()).map((s) => ({
    value: s.code,
    label: s.label,
  }));
});
const template_categories = ref([]);

const self_user = computed(() => store.state.self_user);

const mappedDocuments = computed(() => {
  return (list.value || []).map((item) => ({
    id: item.id || item.uuid,
    number: item.number || item.uuid || item.id,
    status: item.status,
    statusLabel: getStatusLabel(item),
    statusColor: getStatusColor(item),
    date: formatDate(item.registration_date),
    created_at: formatDate(item.created_at),
    title: getReferenceTitle(item.template?.code) || item.template_name,
    notary: getNotaryName(item),
    participants: item.participants || [],
  }));
});

watch(
  filters,
  () => {
    filters.page = 1;
    fetchList();
  },
  { deep: true }
);

async function fetchList() {
  loading.value = true;
  try {
    const params = {
      page: filters.page,
      size: filters.size,
    };
    if (filters.template_code) {
      params.template_code = filters.template_code;
    }
    if (filters.reg_date) {
      params.reg_date = filters.reg_date;
    }
    if (filters.category_code) {
      params.category_code = filters.category_code;
    }
    if (filters.status) {
      params.status = filters.status;
    }
    if (sortType.value === "date_desc") {
      params.order_by = "-registration_date";
    } else if (sortType.value === "date_asc") {
      params.order_by = "registration_date";
    } else if (sortType.value === "created_at_desc") {
      params.order_by = "-created_at";
    } else if (sortType.value === "created_at_asc") {
      params.order_by = "created_at";
    }

    const data = await getParticipantNotarialList(params);
    if (data?.success && data.payload) {
      const items = data.payload?.items || data.payload?.participants || [];
      list.value = items;
      total.value = data.payload?.total || items.length;

      const templateCodes = items
        .map((item) => item.template?.code)
        .filter((code) => code != null);

      const codes = [...new Set([...templateCodes])];
      if (codes.length) {
        await loadReferences(codes);
      }
    }
  } finally {
    loading.value = false;
  }
}

async function loadUser() {
  const data = await getMe();
  if (!data?.payload) return;
  const payload = data.payload;
  const firstName = payload.first_name || "";
  const lastName = payload.last_name || "";
  const middleName = payload.middle_name || "";
  user.value = {
    fullName: [lastName, firstName, middleName].filter(Boolean).join(" "),
    iin: payload.identifier || "",
  };
}

function onDirectorySelect(val) {
  tempFilters.template_code = val || null;
  directoryPopoverOpen.value = false;
  applyFilters();
}

function onDirectoryClear() {
  tempFilters.template_code = null;
  directorySearchTerm.value = "";
  applyFilters();
}

function getDirectoryName(code) {
  if (!code) return "";
  const found = directories.value.find((d) => String(d.code) === String(code));
  return found?.name || code;
}

async function searchDirectories(query) {
  if (directoriesSearchTimer.value) {
    clearTimeout(directoriesSearchTimer.value);
  }

  directoriesHasMore.value = false;
  directoriesLoading.value = true;

  directoriesSearchTimer.value = setTimeout(async () => {
    directoriesSearchQuery.value = query;
    directoriesPage.value = 1;
    await loadDirectories();
  }, 300);
}

async function loadDirectories() {
  directoriesLoading.value = true;
  try {
    const params = {
      page: directoriesPage.value,
      size: directoriesPageSize.value,
    };

    if (directoriesSearchQuery.value) {
      params.q = directoriesSearchQuery.value;
    }

    const data = await getDirectoryByParentAllChildren(
      "notarial_action_category",
      params
    );

    if (data?.success) {
      if (directoriesPage.value === 1) {
        directories.value = data.payload?.items || [];
      } else {
        directories.value = [
          ...directories.value,
          ...(data.payload?.items || []),
        ];
      }

      const totalItems = data.payload?.total || 0;
      const loadedItems = directories.value.length;
      directoriesHasMore.value = loadedItems < totalItems;
    }
  } finally {
    directoriesLoading.value = false;
  }
}

async function loadMoreDirectories() {
  if (!directoriesHasMore.value || directoriesLoadingMore.value) return;
  directoriesLoadingMore.value = true;
  directoriesPage.value++;
  try {
    const params = {
      page: directoriesPage.value,
      size: directoriesPageSize.value,
    };
    if (directoriesSearchQuery.value) {
      params.q = directoriesSearchQuery.value;
    }
    const data = await getDirectoryByParentAllChildren(
      "notarial_action_category",
      params
    );
    if (data?.success) {
      directories.value = [
        ...directories.value,
        ...(data.payload?.items || []),
      ];
      const totalItems = data.payload?.total || 0;
      directoriesHasMore.value = directories.value.length < totalItems;
    }
  } finally {
    directoriesLoadingMore.value = false;
  }
}

function openFilterSheet() {
  tempFilters.status = filters.status;
  tempFilters.reg_date = filters.reg_date;
  tempFilters.category_code = filters.category_code;
  showFilterSheet.value = true;
}

function applyFilters() {
  filters.status = tempFilters.status;
  filters.reg_date = tempFilters.reg_date;
  filters.category_code = tempFilters.category_code;
  filters.template_code = tempFilters.template_code;
  sortType.value = tempSortType.value;
  filters.page = 1;
  showFilterSheet.value = false;
  fetchList();
}

function resetFilters() {
  tempFilters.status = null;
  tempFilters.reg_date = null;
  tempFilters.category_code = null;
  tempSortType.value = "created_at_desc";
  applyFilters();
}

function updateFilters() {
  applyFilters();
}

function openSortSheet() {
  tempSortType.value = sortType.value;
  showSortSheet.value = true;
}

function applySort() {
  sortType.value = tempSortType.value;
  filters.page = 1;
  showSortSheet.value = false;
  fetchList();
}

function toggleFilters() {
  tempFilters.status = filters.status;
  tempFilters.reg_date = filters.reg_date;
  tempFilters.category_code = filters.category_code;
  tempFilters.template_code = filters.template_code;
  tempSortType.value = sortType.value;
  showFilters.value = !showFilters.value;
}

async function loadReferences(codes) {
  const data = await getReferenceListByCodes({ codes });
  if (data?.success) {
    references.value = data.payload?.items || [];
  }
}

function getReferenceTitle(code) {
  if (!code) return t("Не указано");
  const reference = references.value.find((item) => item.code === code);
  return reference?.name || code;
}

function getStatusKey(item) {
  return item.status?.code || item.status?.value || item.status || "";
}

function getStatusLabel(item) {
  const key = getStatusKey(item);
  return getStatusLabelFromDict(key) || t("Не указано");
}

function getStatusColor(item) {
  return getStatusColorFromDict(getStatusKey(item));
}

function getNotaryName(item) {
  if (item.notary_data?.full_name) return item.notary_data.full_name;
  const first = item.notary_data?.first_name || "";
  const last = item.notary_data?.last_name || "";
  const middle = item.notary_data?.middle_name || "";
  const full = [last, first, middle].filter(Boolean).join(" ");
  if (full) return full;
  return item.notary_data?.name || item.notary_data?.fio || t("Не указано");
}

function formatDate(date) {
  if (!date) return t("Не указано");
  return dateFormat.timestamp_to_local_tz(date, "DD.MM.YYYY, HH:mm");
}

function handlePageChange(page) {
  filters.page = page;
  fetchList();
}

function findMeParticipant(document) {
  return document.participants.find(
    (participant) => participant.identifier === self_user.value?.identifier
  );
}

function openItem(row) {
  const id = row?.id || row?.uuid;
  const me_participant = findMeParticipant(row);
  if (!id) return;
  router.push({
    name: "client_document_view",
    params: { id },
    query: {
      participant_uuid: me_participant?.uuid,
      template_category_code: row.template_category_code,
    },
  });
}

function openMobileItem(document) {
  const me_participant = findMeParticipant(document);
  if (!document?.id) return;
  router.push({
    name: "client_document_view",
    params: { id: document.id },
    query: {
      participant_uuid: me_participant?.uuid,
      template_category_code: document.template_category_code,
    },
  });
}

onMounted(async () => {
  ensureStatusDictionary();
  await fetchList();
  await loadDirectories();
  const [template_categories_data] = await Promise.all([
    getDirectoryItem("notarial_action_category"),
  ]);
  template_categories.value = template_categories_data?.payload?.children || [];
  await loadUser();
});
</script>

<template>
  <div class="wrapper-page">
    <div class="flex flex-col h-full w-full gap-8">
      <div class="web-header">
        <ClientCabinetProfile :user="user" />
        <ClientCabinetQuickActions
          :actions="quickActions"
          @action="handleQuickAction"
        />
      </div>
      <div class="mobile-header">
        <ClientCabinetProfile :user="user" />
        <ClientCabinetQuickActions
          :actions="quickActions"
          @action="handleQuickAction"
        />
      </div>

      <div class="flex flex-col gap-3">
        <div class="filters">
          <div class="filters-bar">
            <div class="filters-title">{{ $t("Нотариальные документы") }}</div>
            <div class="filters-controls">
              <div class="search">
                <Popover v-model:open="directoryPopoverOpen">
                  <PopoverTrigger as-child>
                    <div
                      class="flex w-full items-center gap-2 rounded-md border border-input bg-transparent px-3 h-9 cursor-pointer"
                    >
                      <SearchIcon
                        class="size-4 shrink-0 text-muted-foreground"
                      />
                      <span
                        v-if="tempFilters.template_code"
                        class="flex-1 text-sm leading-none truncate"
                      >
                        {{ getDirectoryName(tempFilters.template_code) }}
                      </span>
                      <span
                        v-else
                        class="flex-1 text-sm leading-none text-muted-foreground"
                      >
                        {{ $t("Поиск по названию документа") }}
                      </span>
                      <button
                        v-if="tempFilters.template_code"
                        type="button"
                        class="shrink-0 rounded-sm opacity-50 hover:opacity-100"
                        @click.stop.prevent="onDirectoryClear"
                      >
                        <X class="size-3.5" />
                      </button>
                      <ChevronDown class="size-4 shrink-0 opacity-50" />
                    </div>
                  </PopoverTrigger>
                  <PopoverContent
                    class="w-[var(--reka-popover-trigger-width)] p-0"
                    align="start"
                  >
                    <div class="flex items-center gap-2 border-b px-3 h-9">
                      <SearchIcon
                        class="size-4 shrink-0 text-muted-foreground"
                      />
                      <input
                        v-model="directorySearchTerm"
                        :placeholder="$t('Введите для поиска...')"
                        class="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                        @input="searchDirectories(directorySearchTerm)"
                      />
                    </div>
                    <div class="max-h-60 overflow-y-auto p-1">
                      <div
                        v-if="directoriesLoading"
                        class="py-6 text-center text-sm text-muted-foreground"
                      >
                        {{ $t("Загрузка...") }}
                      </div>
                      <div
                        v-else-if="!directories.length"
                        class="py-6 text-center text-sm text-muted-foreground"
                      >
                        {{ $t("Ничего не найдено") }}
                      </div>
                      <template v-else>
                        <button
                          v-for="item in directories"
                          :key="item.code"
                          type="button"
                          :class="[
                            'flex w-full items-center justify-between rounded-sm px-2.5 py-2.5 text-sm text-left cursor-pointer hover:bg-accent hover:text-accent-foreground',
                            String(item.code) ===
                              String(tempFilters.template_code) &&
                              'bg-accent text-accent-foreground',
                          ]"
                          @click="onDirectorySelect(String(item.code))"
                        >
                          <span class="flex-1 text-sm leading-5">{{
                            item.name
                          }}</span>
                          <Check
                            v-if="
                              String(item.code) ===
                              String(tempFilters.template_code)
                            "
                            class="size-4 shrink-0"
                          />
                        </button>
                        <div v-if="directoriesHasMore" class="text-center p-2">
                          <button
                            type="button"
                            @click.stop="loadMoreDirectories"
                            :disabled="directoriesLoadingMore"
                            class="text-muted-foreground hover:text-foreground text-sm font-medium disabled:opacity-50"
                          >
                            {{
                              directoriesLoadingMore
                                ? $t("Загрузка...")
                                : $t("Загрузить еще")
                            }}
                          </button>
                        </div>
                      </template>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
              <div class="filters-actions">
                <Button variant="outline" @click="toggleFilters" size="icon">
                  <svg-icon
                    name="huge-filter"
                    class="w-5 h-5 text-muted-foreground"
                  />
                </Button>
                <button
                  type="button"
                  class="filter-button"
                  @click="openFilterSheet"
                >
                  <span>{{ $t("Фильтр") }}</span>
                  <svg-icon name="huge-filter" class="w-4 h-4 text-red-700" />
                </button>
                <button
                  type="button"
                  class="filter-button"
                  @click="openSortSheet"
                >
                  <span>{{ $t("Сортировка") }}</span>
                  <svg-icon
                    name="huge-arrow-up-down"
                    class="w-4 h-4 text-red-700"
                  />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div
          class="filter-container"
          :class="{ 'filter-hidden': !showFilters }"
        >
          <div class="filter-wrapper">
            <div class="filter-panel">
              <ClientCabinetFilter
                v-model="tempFilters"
                :sort-value="tempSortType"
                :status-options="statusOptions"
                :template-categories="template_categories"
                @change="updateFilters"
                @update:sortValue="(val) => (tempSortType = val)"
                @reset="resetFilters"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="table-area">
        <div class="h-full overflow-auto">
          <Table
            class="main-table cursor-pointer"
            :loading="loading"
            :scroll-reset-key="filters.page"
          >
            <TableHeader>
              <TableRow>
                <TableHead class="min-w-[80px]">{{ $t("ID") }}</TableHead>
                <TableHead class="min-w-[300px]">{{
                  $t("№  нотариального действия")
                }}</TableHead>
                <TableHead class="min-w-[330px]">{{
                  $t("Вид нотариального действия")
                }}</TableHead>
                <TableHead class="min-w-[200px]">{{
                  $t("Нотариус")
                }}</TableHead>
                <TableHead class="min-w-[200px]">{{
                  $t("Дата создания")
                }}</TableHead>
                <TableHead class="min-w-[200px]">{{
                  $t("Дата и время")
                }}</TableHead>
                <TableHead class="min-w-[160px]">{{ $t("Статус") }}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableEmpty v-if="!loading && !list.length" :colspan="7">
                Нет данных
              </TableEmpty>
              <TableRow
                v-for="row in list"
                :key="row.id || row.uuid"
                @click="openItem(row)"
              >
                <TableCell>
                  <div class="flex items-start justify-start py-2">
                    {{ row.id }}
                  </div>
                </TableCell>
                <TableCell>
                  <div class="flex items-start justify-start">
                    {{ row.number }}
                  </div>
                </TableCell>
                <TableCell>
                  <div class="flex items-start justify-start">
                    {{ getReferenceTitle(row.template?.code) }}
                  </div>
                </TableCell>
                <TableCell>
                  {{ getNotaryName(row) }}
                </TableCell>
                <TableCell>
                  {{ formatDate(row?.created_at) }}
                </TableCell>
                <TableCell>
                  {{ formatDate(row?.registration_date) }}
                </TableCell>
                <TableCell>
                  <div class="flex items-center justify-start gap-2">
                    <StatusDot :code="getStatusKey(row)" />
                    <span>{{ getStatusLabel(row) }}</span>
                  </div>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>

        <TableFooterWrapper
          v-if="total > filters.size"
          :total="total"
          v-model:pageSize="filters.size"
          v-model:currentPage="filters.page"
          :pageSizes="[1, 5, 10, 20]"
          :page-size="filters.size"
        >
          <template #pagination>
            <Pagination
              :total="total"
              :items-per-page="filters.size"
              :page="filters.page"
              :sibling-count="1"
              :disabled="loading"
              show-edges
              @update:page="handlePageChange"
            >
              <PaginationContent v-slot="{ items }">
                <PaginationPrevious />
                <template v-for="(item, index) in items" :key="index">
                  <PaginationItem
                    v-if="item.type === 'page'"
                    :value="item.value"
                    :is-active="item.value === filters.page"
                  />
                  <PaginationEllipsis v-else />
                </template>
                <PaginationNext />
              </PaginationContent>
            </Pagination>
          </template>
        </TableFooterWrapper>
      </div>

      <div class="mobile-list">
        <div v-if="loading" class="flex items-center justify-center py-6">
          <Spinner size="sm" />
        </div>
        <div v-else-if="!mappedDocuments.length" class="empty">
          {{ $t("Документы не найдены") }}
        </div>
        <ClientCabinetDocumentCard
          v-else
          v-for="document in mappedDocuments"
          :key="`${document.id}-${document.statusLabel}`"
          :document="document"
          @open="openMobileItem(document)"
        />
      </div>

      <BottomSheet
        v-model="showFilterSheet"
        :title="$t('Фильтр')"
        height="auto"
        width="auto"
      >
        <div class="flex flex-col gap-3">
          <p class="form-description">
            {{ $t("Выберите необходимые фильтры") }}
          </p>

          <FormItem float>
            <FormControl>
              <Input
                v-model="tempFilters.reg_date"
                type="date"
                class="w-full"
              />
            </FormControl>
            <FormLabel>{{ $t("Дата сделки") }} *</FormLabel>
          </FormItem>

          <FormItem float>
            <FormControl>
              <Select v-model="tempFilters.status">
                <SelectTrigger
                  class="w-full"
                  @clear="tempFilters.status = null"
                >
                  <SelectValue :placeholder="$t('Выберите статус')" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem
                    v-for="status in statusOptions"
                    :key="status.value"
                    :value="String(status.value)"
                  >
                    {{ status.label }}
                  </SelectItem>
                </SelectContent>
              </Select>
            </FormControl>
            <FormLabel>{{ $t("Статус") }} *</FormLabel>
          </FormItem>

          <FormItem float>
            <FormControl>
              <Select v-model="tempFilters.category_code">
                <SelectTrigger
                  class="w-full"
                  @clear="tempFilters.category_code = null"
                >
                  <SelectValue :placeholder="$t('Тип услуги')" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem
                    v-for="item in template_categories"
                    :key="item.code"
                    :value="String(item.code)"
                  >
                    {{ item.name }}
                  </SelectItem>
                </SelectContent>
              </Select>
            </FormControl>
            <FormLabel>{{ $t("Тип услуги") }}</FormLabel>
          </FormItem>

          <Button variant="default" class="w-full mt-2" @click="applyFilters">
            {{ $t("Подтвердить") }}
          </Button>
        </div>
      </BottomSheet>

      <BottomSheet
        v-model="showSortSheet"
        :title="$t('Сортировка')"
        height="auto"
        width="auto"
      >
        <div class="flex flex-col gap-3 align-start">
          <p class="form-description">{{ $t("Выберите тип сортировки") }}</p>

          <RadioGroup
            v-model="tempSortType"
            class="flex flex-col gap-3"
            style="align-items: flex-start"
          >
            <Radio value="created_at_desc" size="large">
              {{ $t("Дата создания: новые") }}
            </Radio>
            <Radio value="created_at_asc" size="large">
              {{ $t("Дата создания: старые") }} </Radio
            ><Radio value="date_desc" size="large">
              {{ $t("Дата регистрации: новые") }}
            </Radio>
            <Radio value="date_asc" size="large">
              {{ $t("Дата регистрации: старые") }}
            </Radio>
          </RadioGroup>

          <Button variant="default" class="w-full mt-2" @click="applySort">
            {{ $t("Подтвердить") }}
          </Button>
        </div>
      </BottomSheet>
    </div>
  </div>
</template>

<style scoped lang="scss">
.web-header {
  @apply w-full flex flex-col gap-6;
}

.mobile-header {
  display: none;
}

.filters {
  @apply w-full flex items-end gap-4;
}

.filters-bar {
  @apply w-full flex items-center justify-between gap-4;
}

.filters-title {
  @apply text-foreground text-lg font-semibold w-full;
}

.filters-controls {
  @apply flex items-center gap-4 justify-end;
  @apply w-full;
}

.search {
  @apply w-full;
  max-width: 420px;
}

.filters-actions {
  @apply flex items-center gap-2.5;
}

.filter-icon {
  @apply p-2 rounded-md border border-solid border-border bg-card cursor-pointer flex items-center justify-center;
  &:hover {
    @apply bg-muted;
  }
}

.filter-button {
  @apply px-3 py-2.5 bg-card rounded-xl outline outline-1 outline-border;
  @apply flex items-center gap-3 text-sm text-foreground;
  display: none;
}

.filter-container {
  @apply w-full;
}

.filter-wrapper {
  @apply w-full transition-all;
}

.filter-hidden {
  display: none;
}

.filter-panel {
  @apply w-full bg-card rounded-2xl;
}

.mobile-list {
  display: none;
}

.empty {
  @apply text-sm text-muted-foreground text-center py-6;
}

@media (max-width: 900px) {
  .table-area {
    display: none;
  }

  .web-header {
    display: none;
  }

  .mobile-header {
    @apply w-full flex flex-col gap-6;
    display: flex;
  }

  .filter-icon,
  .filter-container {
    display: none;
  }

  .filter-button {
    display: flex;
  }

  .filters {
    @apply flex-col items-stretch;
  }

  .filters-bar {
    @apply flex-col items-stretch;
  }

  .filters-controls {
    @apply flex-col items-stretch;
  }

  .search {
    max-width: 100%;
  }

  .mobile-list {
    @apply flex flex-col gap-3 h-full overflow-y-auto;
    display: flex;
  }
}

.form-description {
  @apply text-sm text-muted-foreground font-normal leading-5;
}
</style>
